import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';
import UberDirectSettings from './UberDirectSettings';
import './BeclickDeliverySettings.css';

const endpoint = `${API_URL}/api/admin/delivery/uber-partner`;
function session() {
  try {
    const account = JSON.parse(localStorage.getItem('kioskAccount') || 'null');
    return JSON.stringify([getStoredAuthToken(), String(account?.id || ''), String(account?.tenantAccountId || '')]);
  } catch { return null; }
}
const stopped = () => Object.assign(new Error('Cette consultation est fermée ou sa session a changé.'), { status: 403 });

export default function BeclickDeliverySettings({ allowed = false, fetcher = fetch }) {
  const [owner] = useState(session), [opened, setOpened] = useState(0), [error, setError] = useState('');
  const epoch = useRef(0), pending = useRef(new Set()), permission = useRef(allowed), locked = useRef(false);
  permission.current = allowed;
  const close = useCallback(() => {
    epoch.current += 1;
    for (const controller of pending.current) controller.abort();
    pending.current.clear(); setOpened(0);
  }, []);
  const invalidate = useCallback(message => { locked.current = true; close(); setError(message); }, [close]);
  const guard = useCallback(() => {
    if (locked.current) return false;
    if (!permission.current || !getStoredAuthToken() || owner === null || session() !== owner) {
      invalidate('La session ou l’accès a changé. Les informations de livraison ont été retirées. Rechargez Mes options pour vérifier vos droits.'); return false;
    }
    return true;
  }, [owner, invalidate]);
  useEffect(() => {
    const controllers = pending.current;
    const check = () => { guard(); };
    const timer = window.setInterval(check, 250); window.addEventListener('storage', check); window.addEventListener('focus', check);
    return () => {
      epoch.current += 1;
      for (const controller of controllers) controller.abort();
      controllers.clear(); window.clearInterval(timer); window.removeEventListener('storage', check); window.removeEventListener('focus', check);
    };
  }, [guard]);
  useEffect(() => { if (!allowed) invalidate('L’accès à la livraison BECLICK a été retiré. Rechargez Mes options pour vérifier vos droits.'); }, [allowed, invalidate]);
  const request = useCallback(async (url, options = {}) => {
    const method = options.method || 'GET';
    const supported = (url === endpoint && ['GET', 'PUT'].includes(method)) || ([`${endpoint}/onboarding`, `${endpoint}/refresh`].includes(url) && method === 'POST');
    const current = () => opened !== 0 && opened === epoch.current && guard();
    if (!supported || !current()) throw stopped();
    const controller = new AbortController(); pending.current.add(controller);
    const valid = () => !controller.signal.aborted && current();
    try {
      const response = await fetcher(url, { ...options, method, headers: buildKioskAuthHeaders(options.headers), cache: 'no-store', signal: controller.signal });
      if (!valid()) throw stopped();
      if ([401, 402, 403].includes(response.status)) {
        invalidate('Accès livraison refusé. Les informations ont été retirées. Rechargez Mes options pour vérifier votre session.'); throw stopped();
      }
      // Keep the boundary around body parsing too: the original form awaits JSON.
      return { ok: response.ok, status: response.status, json: async () => {
        try {
          if (!valid()) throw stopped();
          const payload = await response.json();
          if (!valid()) throw stopped();
          return payload;
        } catch (failure) { if (!valid()) throw stopped(); throw failure; }
        finally { pending.current.delete(controller); }
      } };
    } catch (failure) {
      pending.current.delete(controller);
      if (!valid()) throw stopped();
      throw failure;
    }
  }, [opened, guard, invalidate, fetcher]);
  if (!allowed) return null;
  const interaction = event => { if (!guard()) { event.preventDefault(); event.stopPropagation(); } };
  return <section className="beclick-delivery-settings" aria-label="Livraison BECLICK">
    <h3>Livraison BECLICK</h3>
    <p>Préparez votre facturation et le point de retrait, puis retrouvez les étapes de connexion Uber Direct. Aucune invitation n’est envoyée à l’ouverture.</p>
    {error && <p role="alert">{error}</p>}
    <button className="product-options-settings" type="button" disabled={locked.current} aria-expanded={Boolean(opened)} onClick={() => {
      if (!guard()) return;
      if (opened) close(); else { const next = ++epoch.current; setError(''); setOpened(next); }
    }}>{opened ? 'Fermer la livraison' : 'Configurer la livraison'}</button>
    {opened !== 0 && <div onClickCapture={interaction} onKeyDownCapture={interaction} onSubmitCapture={interaction}>
      <p>Fermer ce panneau abandonne les modifications non enregistrées. Une demande déjà envoyée peut continuer côté serveur ; relisez son état après réouverture.</p>
      <UberDirectSettings key={opened} request={request} scopeKey="options-delivery" />
    </div>}
  </section>;
}
